import { getMyFilesNoSession, searchMyFilesNoSession } from "~/api/library"
import { ITokenInfo } from "~/types/global"
import { IUpdateRfpsWorkflow } from "~/types/rfpos"
import { axiosInstance, retryFetch } from "~/utils/axios"

export async function getCopilotSessions(tokenInfo: ITokenInfo) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/sessions?feature=PROPOSAL`
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getCopilotSessionDetail(
  tokenInfo: ITokenInfo,
  sessionId: string
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/session/${sessionId}?hide_extracted_files=true`
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getCopilotSessionContributors(
  tokenInfo: ITokenInfo,
  params: {
    session_id?: string
  } = {}
) {
  const { session_id } = params
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/rfp/${session_id}/shared_users`
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getCopilotSessionsPaginated(
  tokenInfo: ITokenInfo,
  params: {
    skip?: number
    limit?: number
    search?: string
  } = {}
) {
  const { skip = 0, limit = 20, search = "" } = params
  const searchParams = new URLSearchParams({
    feature: "PROPOSAL",
    skip: skip.toString(),
    limit: limit.toString(),
    _t: Date.now().toString(),
    ...(search && { search })
  })

  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/sessions?${searchParams.toString()}`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function createCopilotSession(
  tokenInfo: ITokenInfo,
  payload: any
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/session`
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: payload
  })
}

export async function getBriefing(
  tokenInfo: ITokenInfo,
  sessionId: string,
  { fileId, shortened = false }: { fileId?: string; shortened?: boolean }
) {
  let url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/proposal/${sessionId}`
  if (fileId) {
    url = `${url}/file/${fileId}`
  }
  if (shortened) {
    url = `${url}/shorten_briefing`
  } else {
    url = `${url}/briefing`
  }
  return await axiosInstance({
    method: fileId ? "POST" : "GET",
    url,
    headers: {
      "Content-Type": "application/json;",
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getCopilotTemplates(tokenInfo: ITokenInfo) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/templates`
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function downloadRfpOutlineExcel(
  tokenInfo: ITokenInfo,
  sessionId: string,
  fileId: string
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/qna/${sessionId}/outlineV2?file_id=${encodeURIComponent(fileId)}`
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function downloadRfpQnaWordDoc(
  tokenInfo: ITokenInfo,
  sessionId: string,
  fileId: string,
  isWordDoc: boolean,
  payload: any
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${
    tokenInfo?.user_id
  }/copilot/qna/${sessionId}/${
    isWordDoc ? "download_qna" : "download_table_qna"
  }?file_id=${encodeURIComponent(fileId)}`
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: payload
  })
}

export async function getRfpQuestionnaireExtraction(
  tokenInfo: ITokenInfo,
  input_string: string,
  options?: {
    mode?: "manual" | "auto_detect"
    table_data?: string
  }
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/rfp_questionnarie_extration`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      input_string,
      ...(options?.mode && { mode: options.mode }),
      ...(options?.table_data && { table_data: options.table_data })
    }
  })
}

export async function addOutlineQuestions(
  tokenInfo: ITokenInfo,
  sessionId: string,
  payload: {
    file_id: string
    outline_payload: Array<{
      section: string
      questions: Array<{ question: string; rowIndex?: number }>
      subsections: Array<{
        subsection: string
        questions: Array<{ question: string; rowIndex?: number }>
      }>
    }>
  }
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/qna/${sessionId}/add_outline_questions`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: payload
  })
}

export async function getTrackAssignees(
  tokenInfo: ITokenInfo,
  sessionId: string
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/qna/${sessionId}/track_assignees`
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getQnaContributors(
  tokenInfo: ITokenInfo,
  sessionId: string,
  activePdfId: string
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/qna/${sessionId}/file/${activePdfId}/contributors`
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getQnaContributorAssignmentIds(
  tokenInfo: ITokenInfo,
  sessionId: string,
  fileId: string
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/qna/${sessionId}/file/${fileId}/contributors?contributor_id=${tokenInfo?.user_id}`
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

// export async function generateQnaOutline(
//   tokenInfo: ITokenInfo,
//   sessionId: string
// ) {
//   const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/qna/${sessionId}/outline`
//   return await axiosInstance({
//     method: "POST",
//     url,
//     headers: {
//       Authorization: `Bearer ${tokenInfo?.token}`
//     },
//     data: {}
//   })
// }

export async function getAnswerRequisites(
  tokenInfo: ITokenInfo,
  sessionId: string
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/qna/${sessionId}/poll/answer_requisites`
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getSearchOfferings(
  tokenInfo: ITokenInfo,
  sessionId: string,
  tagsFilter: any,
  size: number = 5000,
  offset: number = 0
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/library/search`
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      "Content-Type": "application/json;charset=utf-8",
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      query: "",
      offset: offset,
      size: size,
      tagIds: tagsFilter
    }
  })
}
export async function getScopedSearchOfferings(
  tokenInfo: ITokenInfo,
  sessionId: string,
  tagsFilter: any,
  show_my_files_in_rfp_response: boolean = false
) {
  const url = `tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/rfp/${sessionId}/library/scoped_search`
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      "Content-Type": "application/json;charset=utf-8",
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      query: "",
      offset: 0,
      size: 5000,
      tagIds: tagsFilter,
      show_my_files_in_rfp_response
    }
  })
}

export async function getExecutiveSummary(
  tokenInfo: ITokenInfo,
  sessionId: string,
  regenerate: boolean = false
) {
  let url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/qna/${sessionId}/executive_summary`
  if (regenerate) url += "?regenerate=true"
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      "Content-Type": "application/json;charset=utf-8",
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getKeyExecConfig(
  tokenInfo: ITokenInfo,
  sessionId: string
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/qna/${sessionId}/people`
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getRfpLevelGuidance(
  tokenInfo: ITokenInfo,
  sessionId: string,
  fileId: string
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/qna/${sessionId}/file/${fileId}/guidance`
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getRFPdocsQnaSession({
  tokenInfo,
  sessionId,
  chat_id
}: {
  tokenInfo: ITokenInfo
  sessionId: string
  chat_id: string
}) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/proposal/${sessionId}/rfp_docs_qna/${chat_id}/history`
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getRfpSessionShareUsers(
  tokenInfo: ITokenInfo,
  item: any
) {
  let url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/rfp_sharing/users`
  if (item) {
    const encodedItem = encodeURIComponent(item)
    url += `?item=${encodedItem}`
  }
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function shareRfpWithUsers(
  tokenInfo: ITokenInfo,
  data: {
    tenant_id: number
    shared_ids: number[]
    id: number
    user_id: number
  }
) {
  return await axiosInstance({
    method: "POST",
    url: `/copilot/rfp_sharing/share`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data
  })
}

export async function removeAccessRfpUser(
  tokenInfo: ITokenInfo,
  data: {
    id: string
    item: string
    tenant_id: string
  }
) {
  return await axiosInstance({
    method: "POST",
    url: `/copilot/rfp_sharing/remove_access`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data
  })
}

export async function generateBriefingCustom(
  tokenInfo: ITokenInfo,
  sessionId: string,
  { fileId, sectionIds }: { fileId?: string; sectionIds?: string[] }
) {
  let url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/proposal/${sessionId}`
  if (fileId) {
    url = `${url}/file/${fileId}`
  }
  url = `${url}/generate-briefing`
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: sectionIds ? { sectionIds } : undefined
  })
}

export async function shortenBriefingCustom(
  tokenInfo: ITokenInfo,
  sessionId: string,
  { fileId }: { fileId?: string }
) {
  let url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/proposal/${sessionId}`
  if (fileId) {
    url = `${url}/file/${fileId}`
  }
  url = `${url}/shorten_briefing-custom`
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function createRfpBriefingSection(
  tokenInfo: ITokenInfo,
  data: {
    tenant_id: string
    user_id: string
    section_name: string
    section_description: string
    scope: "RFP" | "USERSESSION" | "TENANT"
    rfp_id?: string
  }
) {
  const {
    tenant_id,
    user_id,
    section_name,
    section_description,
    scope,
    rfp_id
  } = data
  let url = `/tenants/${tenant_id}/users/${user_id}/briefing_sections`
  if (rfp_id) {
    url += `?rfp_id=${rfp_id}`
  }

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      section_name,
      section_description,
      scope
    }
  })
}

export async function updateRfpBriefingSection(
  tokenInfo: ITokenInfo,
  data: {
    tenant_id: string
    user_id: string
    section_id: string
    section_name?: string
    section_description?: string
  }
) {
  const { tenant_id, user_id, section_id, section_name, section_description } =
    data
  const url = `/tenants/${tenant_id}/users/${user_id}/briefing_sections/${section_id}`

  return await axiosInstance({
    method: "PUT",
    url,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      section_name,
      section_description
    }
  })
}

export async function deleteRfpBriefingSection(
  tokenInfo: ITokenInfo,
  data: {
    tenant_id: string
    user_id: string
    section_id: string
  }
) {
  const { tenant_id, user_id, section_id } = data
  const url = `/tenants/${tenant_id}/users/${user_id}/briefing_sections/${section_id}`

  return await axiosInstance({
    method: "DELETE",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function hideRfpBriefingSection(
  tokenInfo: ITokenInfo,
  data: {
    tenant_id: string
    user_id: string
    section_id: string
    rfp_id: string
    is_visible: boolean
  }
) {
  const { tenant_id, user_id, section_id, rfp_id, is_visible } = data
  const url = `/tenants/${tenant_id}/users/${user_id}/briefing_sections/${section_id}/hide?rfp_id=${rfp_id}&is_visible=${is_visible}`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getBriefingChats(
  tokenInfo: ITokenInfo,
  sessionId: string
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/proposal/${sessionId}/rfp_docs_qna/chats`
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getLatestBriefingChatId(
  tokenInfo: ITokenInfo,
  sessionId: string
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/proposal/${sessionId}/rfp_docs_qna/latest_chat_id`
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getQnaResponseSources({
  tokenInfo,
  sessionId,
  questionId,
  showMyFiles
}: {
  tokenInfo: ITokenInfo
  sessionId: string
  questionId: string
  showMyFiles?: boolean
}) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/qna/${sessionId}/sources/${questionId}`
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    params: {
      ...(showMyFiles !== undefined && {
        show_my_files_in_rfp_response: showMyFiles
      })
    }
  })
}

export async function deleteSessionUploadedFile({
  tokenInfo,
  sessionId,
  fileIds,
  is_user_preference,
  show_my_files_in_rfp_response
}: {
  tokenInfo: any
  sessionId: any
  fileIds: string[]
  is_user_preference?: boolean
  show_my_files_in_rfp_response?: boolean
}) {
  if (!sessionId || !fileIds.length || !tokenInfo) return
  return await axiosInstance({
    method: "POST",
    url: `tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/session/${sessionId}/delete_scoped_files`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      file_ids: fileIds,
      ...(is_user_preference && { is_user_preference }),
      ...(show_my_files_in_rfp_response !== undefined && {
        show_my_files_in_rfp_response
      })
    }
  })
}

export async function deleteUserScopedFiles(
  tokenInfo: ITokenInfo,
  fileIds: string[]
) {
  return await axiosInstance({
    method: "POST",
    url: `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/delete_user_scoped_files`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      file_ids: fileIds
    }
  })
}

// Get scoped files processing status
export async function getScopedFilesStatus(
  tokenInfo: ITokenInfo,
  subSessionId: string,
  showMyFiles?: boolean
) {
  if (!subSessionId) {
    return Promise.reject(
      new Error("getScopedFilesStatus: subSessionId required")
    )
  }
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/session/${subSessionId}/scoped_files_status`
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    params: {
      ...(showMyFiles !== undefined && {
        show_my_files_in_rfp_response: showMyFiles
      })
    }
  })
}

export async function getAgentSessions(
  tokenInfo: ITokenInfo,
  parentSessionId: string,
  filter: "all" | "my" = "all"
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/agent-sessions?session_parent_id=${parentSessionId}&filter=${filter}`
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`,
      "Content-Type": "application/json;charset=utf-8"
    }
  })
}

export async function createAgentSession(
  tokenInfo: ITokenInfo,
  parentSessionId: string,
  question: string,
  researchEnabled = false
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/session?session_parent_id=${parentSessionId}`
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`,
      "Content-Type": "application/json"
    },
    data: {
      feature: "AGENT",
      question,
      markInactive: true,
      researchEnabled: researchEnabled
    }
  })
}

export async function sendAgentMessage(
  tokenInfo: ITokenInfo,
  subSessionId: string,
  query: string,
  parentSessionId: string,
  files?: { name: string; fileId?: string }[],
  researchEnabled = false,
  contextQuote?: string | null
) {
  const url = `${axiosInstance.defaults.baseURL}/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/rfp/chatbot/session/${subSessionId}`

  const body: any = {
    query,
    parent_session_id: parentSessionId,
    researchEnabled: researchEnabled
  }

  if (contextQuote) {
    body.context_quote = contextQuote
  }

  if (files && files.length > 0) {
    body.files = files.map((f) => ({
      name: f.name,
      id: f.fileId
    }))
  }

  return await retryFetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    body: JSON.stringify(body)
  })
}

export async function convertFile(tokenInfo: ITokenInfo, data: any) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/library/file/convert`
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data
  })
}

export async function getChatSources(
  tokenInfo: ITokenInfo,
  subSessionId: string,
  chatId: string
) {
  return await axiosInstance({
    method: "GET",
    url: `tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/chatbot/session/${subSessionId}/sources/${chatId}`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`,
      "Content-Type": "application/json"
    }
  })
}

export async function notifyUserScopedFilesUploaded(
  tokenInfo: ITokenInfo,
  filesPath: string[]
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/user_scoped_files_uploaded`
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`,
      "Content-Type": "application/json"
    },
    data: {
      files: filesPath.map((path) => ({ s3_path: path }))
    }
  })
}

export async function getRfpChatSessionHistory(
  tokenInfo: ITokenInfo,
  sessionId: string,
  subSessionId: string
) {
  return await axiosInstance({
    method: "GET",
    url: `tenant/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/chatbot/session/${subSessionId}/history?parent_session_id=${sessionId}`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`,
      "Content-Type": "application/json"
    }
  })
}

export const generateAnswer = async ({
  tokenInfo,
  sessionId,
  currentOutlineId,
  payload
}: {
  tokenInfo: ITokenInfo
  sessionId: string
  currentOutlineId: string
  payload: any
}) => {
  return await axiosInstance({
    method: "POST",
    url: `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/qna/${sessionId}/question_answer/${currentOutlineId}`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`,
      "Content-Type": "application/json"
    },
    data: payload
  })
}

export const updateQuestionAnswer = async ({
  tokenInfo,
  sessionId,
  currentOutlineId,
  payload,
  method = "POST"
}: {
  tokenInfo: ITokenInfo
  sessionId: string
  currentOutlineId: string
  payload: any
  method: "POST" | "PUT"
}) => {
  return await axiosInstance({
    method,
    url: `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/qna/${sessionId}/question_answer/${currentOutlineId}`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`,
      "Content-Type": "application/json"
    },
    data: payload
  })
}

export const fetchContributorView = async (
  tokenInfo: ITokenInfo,
  {
    sessionId,
    contributorId
  }: {
    sessionId: string
    contributorId: number
  }
) => {
  return await axiosInstance({
    method: "GET",
    url: `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/qna/${sessionId}/contributor_view/${contributorId}`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`,
      "Content-Type": "application/json"
    }
  })
}

export async function toggleArhiveSession(
  tokenInfo: ITokenInfo,
  sessionId: string,
  isArchieved: boolean
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/session/${sessionId}/toggle_archive`
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`,
      "Content-Type": "application/json"
    },
    data: {
      archive: isArchieved
    }
  })
}

export async function updateUserPreferences(
  tokenInfo: ITokenInfo | null,
  data: any
) {
  return await axiosInstance({
    method: "POST",
    url: `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/update_user_pref`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data
  })
}

export async function getUserPreferences(tokenInfo: ITokenInfo | null) {
  return await axiosInstance({
    method: "GET",
    url: `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/get_user_pref`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getSections(tokenInfo: ITokenInfo, sessionId: string) {
  return await axiosInstance({
    method: "GET",
    url: `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/rfp/${sessionId}/sections`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getFileCdnLink(
  tokenInfo: ITokenInfo,
  sessionId: string,
  fileId: string,
  options?: {
    feature?: string
    expiryMinutes?: number
  }
) {
  const { feature = "RFP", expiryMinutes = 5 } = options || {}
  const params = new URLSearchParams({
    feature,
    expiryMinutes: expiryMinutes.toString()
  })
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/session/${sessionId}/files/${fileId}/cdn-link?${params.toString()}`
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

// Get user's own files from knowledge library for RFP
export async function getMyFiles(
  tokenInfo: ITokenInfo,
  sessionId: string,
  params: { search?: string; tagIds?: string[] }
) {
  const { search = "", tagIds = [] } = params
  // No real session yet (new flow passes a non-numeric sentinel) -> use the
  // no-session endpoint so a sentinel session_id never reaches the BE, which
  // keys preferences on a bigint session_id. Real sessions are numeric.
  if (!/^\d+$/.test(String(sessionId))) {
    return await getMyFilesNoSession(tokenInfo, { search, tagIds })
  }
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/rfp/${sessionId}/my-files`
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`,
      "Content-Type": "application/json"
    },
    data: {
      search,
      tagIds
    }
  })
}

// Fetch a presigned download URL for one of the user's RFP library files.
// List endpoints (getMyFiles / searchMyFiles) no longer return cdnLink — callers
// invoke this on demand when the user opens / previews a file.
export async function getMyFileDownloadUrl(
  tokenInfo: ITokenInfo,
  sessionId: string,
  fileId: string
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/rfp/${sessionId}/my-files/${fileId}/download-url`
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function updateRFPWorkflowDocumentCount(
  tokenInfo: ITokenInfo | null,
  payload: IUpdateRfpsWorkflow
) {
  const { session_id, ...restPayload } = payload
  return await axiosInstance({
    url: `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/rfps/list/${session_id}`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: restPayload
  })
}

// Search user's own files for RFP with pagination
export async function searchMyFiles(
  tokenInfo: ITokenInfo,
  sessionId: string,
  params: { query?: string; offset?: number; size?: number }
) {
  const { query = "", offset = 0, size = 50 } = params
  if (!/^\d+$/.test(String(sessionId))) {
    return await searchMyFilesNoSession(tokenInfo, { query, offset, size })
  }
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/rfp/${sessionId}/my-files/search`
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`,
      "Content-Type": "application/json"
    },
    data: {
      query,
      offset,
      size
    }
  })
}

// Get shared/preferenced files for RFP
export async function getSharedFiles(
  tokenInfo: ITokenInfo,
  sessionId: string,
  params: { search?: string; tagIds?: string[] }
) {
  const { search = "", tagIds = [] } = params
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/rfp/${sessionId}/shared-files`
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`,
      "Content-Type": "application/json"
    },
    data: {
      search,
      tagIds
    }
  })
}

// Search shared/preferenced files for RFP with pagination
export async function searchSharedFiles(
  tokenInfo: ITokenInfo,
  sessionId: string,
  params: { query?: string; offset?: number; size?: number; tagIds?: string[] }
) {
  const { query = "", offset = 0, size = 50, tagIds = [] } = params
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/rfp/${sessionId}/shared-files/search`
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`,
      "Content-Type": "application/json"
    },
    data: {
      query,
      offset,
      size,
      tagIds
    }
  })
}

// Select files to be used for RFP response generation
export async function selectFilesForRfp(
  tokenInfo: ITokenInfo,
  sessionId: string,
  payload: {
    selectedMyFileIds?: string[]
    unselectedMyFileIds?: string[]
    selectedSharedFileIds?: string[]
    unselectedSharedFileIds?: string[]
    selectedKbFileIds?: string[]
  }
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/rfp/${sessionId}/select-files`
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`,
      "Content-Type": "application/json"
    },
    data: payload
  })
}
